import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  Building2, ClipboardList, Calculator, FileArchive, Send,
  HelpCircle, BookOpen, Ruler, Users, CalendarDays, ShoppingCart,
  ArrowLeftRight, RefreshCcw, Banknote, Award, CreditCard, Cpu,
  PieChart, BarChart3, TrendingUp, Receipt, Folder, Archive, AlertTriangle,
  ArrowUpRight, HardHat
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { fmtCompact } from "@/lib/utils";

const MODULES = [
  { id: "proje", title: "Proje ve Şantiye", icon: Building2, to: "/projeler", key: "projects", color: "amber" },
  { id: "santiye", title: "Firmalar", icon: Users, to: "/firmalar", key: "firms", color: "orange" },
  { id: "is_kalemleri", title: "İş Kalemleri", icon: ClipboardList, to: "/is-kalemleri", key: "work_items", color: "blue" },
  { id: "kesif", title: "Keşif", icon: Calculator, to: "/kesif", key: "discoveries", color: "emerald" },
  { id: "cizimler", title: "Çizimler ve Belgeler", icon: FileArchive, to: "/belgeler", key: "files", color: "slate" },
  { id: "belge_dagitim", title: "Belge Dağıtımı", icon: Send, to: "/belgeler", key: "files", color: "slate" },
  { id: "teknik_sorular", title: "Teknik Sorular", icon: HelpCircle, to: "/belgeler", key: null, color: "slate" },
  { id: "santiye_gunlugu", title: "Şantiye Günlüğü", icon: BookOpen, to: "/santiye-gunlugu", key: "site_logs", color: "amber" },
  { id: "olcumler", title: "Ölçümler", icon: Ruler, to: "/is-kalemleri", key: "work_items", color: "blue" },
  { id: "personel_gorev", title: "Personel Görevlendirme", icon: Users, to: "/puantaj", key: "timesheets", color: "orange" },
  { id: "puantaj", title: "Puantaj", icon: CalendarDays, to: "/puantaj", key: "timesheets", color: "orange" },
  { id: "malzeme_talep", title: "Malzeme Talepleri", icon: ShoppingCart, to: "/malzeme-talepleri", key: "material_requests", color: "blue" },
  { id: "malzeme_hareket", title: "Malzeme Hareketleri", icon: ArrowLeftRight, to: "/malzeme-hareketleri", key: "material_movements", color: "blue" },
  { id: "kesif_rev", title: "Keşif Revizyonu", icon: RefreshCcw, to: "/kesif", key: "discoveries", color: "emerald" },
  { id: "hakedis", title: "Hakediş", icon: Banknote, to: "/hakedis", key: "progress_payments", color: "emerald" },
  { id: "hakedis_sert", title: "Hakediş Sertifikası", icon: Award, to: "/hakedis", key: "progress_payments", color: "emerald" },
  { id: "odeme", title: "Ödeme ve Tahsilat", icon: CreditCard, to: "/hakedis", key: "progress_payments", color: "emerald" },
  { id: "entegrasyon", title: "Entegrasyon İşleri", icon: Cpu, to: "/veri-aktarimi", key: null, color: "slate" },
  { id: "proje_ozeti", title: "Proje Özeti", icon: PieChart, to: "/analiz", key: "projects", color: "amber" },
  { id: "butce", title: "Bütçe ve Gerçekleşme", icon: BarChart3, to: "/analiz", key: null, color: "emerald" },
  { id: "is_ilerleme", title: "İş İlerlemesi", icon: TrendingUp, to: "/analiz", key: null, color: "amber" },
  { id: "hak_odeme", title: "Hakediş ve Ödeme Özeti", icon: Receipt, to: "/analiz", key: "progress_payments", color: "emerald" },
  { id: "personel_evrak", title: "Personel ve Evrak", icon: Folder, to: "/puantaj", key: "timesheets", color: "orange" },
  { id: "stok", title: "Malzeme ve Stok", icon: Archive, to: "/malzeme-hareketleri", key: "material_movements", color: "blue" },
  { id: "onay", title: "Onay Bekleyen Belgeler", icon: AlertTriangle, to: "/malzeme-talepleri", key: "pending_approvals", color: "rose" },
];

const COLOR_MAP = {
  amber: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30",
  orange: "bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/30",
  blue: "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30",
  emerald: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
  slate: "bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/30",
  rose: "bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/30",
};

export default function Dashboard() {
  const [counts, setCounts] = useState({});
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/dashboard/counts").then(r => setCounts(r.data)).catch(() => {});
    api.get("/projects").then(r => setProjects(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Şantiye Yönetimi" subtitle="Tüm şantiye operasyonlarınız tek bir panelde." />

      {/* Hero stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={HardHat} color="amber" label="Projeler" value={counts.projects || 0} testid="stat-projects" />
        <StatCard icon={Users} color="orange" label="Firmalar" value={counts.firms || 0} testid="stat-firms" />
        <StatCard icon={Banknote} color="blue" label="Toplam Hakediş" value={`₺${fmtCompact(counts.total_production)}`} testid="stat-production" />
        <StatCard icon={AlertTriangle} color="rose" label="Onay Bekleyen" value={counts.pending_approvals || 0} testid="stat-pending" />
      </div>

      <h2 className="font-display text-lg font-semibold text-muted-foreground mb-4">Modüller</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {MODULES.map((m, i) => {
          const Icon = m.icon;
          const count = m.key ? (counts[m.key] ?? 0) : null;
          return (
            <Link
              key={m.id}
              to={m.to}
              data-testid={`module-card-${m.id}`}
              className="module-card group relative rounded-xl border border-border bg-card p-5 flex flex-col gap-4"
              style={{ animationDelay: `${i * 20}ms` }}
            >
              <div className="flex items-start justify-between">
                <div className={`w-10 h-10 rounded-lg border flex items-center justify-center ${COLOR_MAP[m.color]}`}>
                  <Icon className="w-5 h-5" />
                </div>
                {count !== null && (
                  <span className="px-2.5 py-1 text-xs font-bold rounded-full bg-slate-900 text-amber-400 border border-slate-800 font-mono-nums">
                    {count}
                  </span>
                )}
              </div>
              <div className="flex items-end justify-between">
                <div className="font-display font-semibold text-foreground leading-tight">{m.title}</div>
                <ArrowUpRight className="w-4 h-4 text-muted-foreground arrow" />
              </div>
            </Link>
          );
        })}
      </div>

      {projects.length === 0 && (
        <div className="mt-10 p-6 rounded-xl border border-dashed border-amber-500/40 bg-amber-500/5 text-sm">
          <div className="font-semibold text-amber-700 dark:text-amber-400 mb-1">İlk projenizi oluşturun</div>
          <p className="text-muted-foreground">Operasyonlara başlamak için "Projeler ve Şantiyeler" sayfasından yeni bir proje ekleyin.</p>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value, testid }) {
  return (
    <div className={`rounded-xl border p-5 ${COLOR_MAP[color]} bg-card`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <Icon className="w-5 h-5" />
      </div>
      <div className="text-3xl font-display font-extrabold font-mono-nums">{value}</div>
      <div className="text-xs mt-1 opacity-80 font-medium">{label}</div>
    </div>
  );
}
